// Explore and publishing, client side (spec 2026-11-02 §4). Every rule lives
// in the SECURITY DEFINER RPCs (migration 20261102090000_creator_explore.sql);
// this layer only calls them and re-validates what comes back.
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  parseChapterDef,
  type ChapterDef,
  type ChapterLevelRef,
  type CreatorChapter,
  type ExploreChapterRow,
  type ExploreRow,
  type PublishedRow,
} from '../config/creator';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

/** Server refusal → copy. Checked IN ORDER: the specific ones before 'banned'. */
export const PUBLISH_ERRORS: [string, string][] = [
  ['not signed in', 'Sign in to publish your levels'],
  ['not ready', 'Your level needs a wave and a plot first'],
  ['already banned', 'That level is already banned'],
  ['not banned', 'That level is not banned'],
  ['banned', 'This level is banned — press ASK to ask for an unban'],
  ['level locked', 'This level is locked while an admin reviews your unban request'],
  ['not found', "Couldn't find that level — save it first"],
  ['not allowed', 'Not allowed'],
  ['no request', 'That request has already been handled'],
  ['request changed', 'This level changed while you were looking — check it again'],
  ['textbox off map', 'A textbox is off the map or too small — move it onto the map first'],
  ['levels not live', 'Every level in a chapter must be live in Explore first'],
  ['chapter empty', 'Add a level to your chapter first'],
  ['bad chapter', "This chapter has something Explore can't show — open it and SAVE it again"],
  ['bad level', "This level has something Explore can't show — open it and SAVE it again"],
];

function friendly(message: string | undefined, fallback: string): string {
  const m = message ?? '';
  for (const [k, v] of PUBLISH_ERRORS) if (m.includes(k)) return v;
  if (m.startsWith('no level')) return `No published level has the code ${m.slice(9)}`;
  if (/must be \d+-\d+/.test(m)) return m.charAt(0).toUpperCase() + m.slice(1);
  return fallback;
}

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// ── The creator's own levels ─────────────────────────────────────────────────

export async function myPublished(c: SupabaseClient): Promise<Result<PublishedRow[]>> {
  const { data, error } = await c.rpc('my_published_levels');
  if (error || !Array.isArray(data)) return { ok: false, error: 'Could not load' };
  return {
    ok: true,
    value: (data as Record<string, unknown>[]).map((r) => ({
      sourceId: str(r.source_id),
      code: str(r.code),
      live: r.live === true,
      banned: r.banned === true,
      requestKind:
        r.request_kind === 'publish' || r.request_kind === 'unban' ? r.request_kind : null,
      plays: num(r.plays),
    })),
  };
}

export async function publishLevel(c: SupabaseClient, sourceId: string): Promise<Result<string>> {
  const { data, error } = await c.rpc('publish_creator_level', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't publish — try again") };
  return { ok: true, value: str(data) };
}

export async function cancelLevelRequest(
  c: SupabaseClient,
  sourceId: string,
): Promise<Result<null>> {
  const { error } = await c.rpc('cancel_level_request', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't cancel — try again") };
  return { ok: true, value: null };
}

export async function askUnban(c: SupabaseClient, sourceId: string): Promise<Result<null>> {
  const { error } = await c.rpc('ask_unban_level', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't ask — try again") };
  return { ok: true, value: null };
}

export interface LevelNotice {
  sourceId: string;
  name: string;
  reason: string;
}

export async function myLevelNotices(c: SupabaseClient): Promise<LevelNotice[]> {
  const { data, error } = await c.rpc('my_level_notices');
  if (error || !Array.isArray(data)) return [];
  return (data as Record<string, unknown>[]).map((r) => ({
    sourceId: str(r.source_id),
    name: str(r.name),
    reason: str(r.reason),
  }));
}

export async function ackLevelNotice(c: SupabaseClient, sourceId: string): Promise<void> {
  await c.rpc('ack_level_notice', { p_source: sourceId });
}

// ── Explore (anyone) ─────────────────────────────────────────────────────────

export function parseExploreRows(data: unknown): ExploreRow[] {
  if (!Array.isArray(data)) return [];
  const out: ExploreRow[] = [];
  for (const r of data as Record<string, unknown>[]) {
    if (!r || typeof r.code !== 'string' || typeof r.name !== 'string') continue;
    out.push({
      code: r.code,
      name: r.name,
      username: str(r.username),
      plays: num(r.plays),
      starOverride: numOrNull(r.star_override),
      hambis: numOrNull(r.hambis),
      trending: r.trending === true,
      background: str(r.background) || 'white',
      template: str(r.template) || 'blank',
      showPicture: r.show_picture !== false,
      verified: num(r.verified),
      border: str(r.border) || 'violet',
    });
  }
  return out;
}

export async function exploreList(c: SupabaseClient): Promise<Result<ExploreRow[]>> {
  const { data, error } = await c.rpc('explore_list');
  if (error) return { ok: false, error: "Couldn't load Explore — check your connection" };
  return { ok: true, value: parseExploreRows(data) };
}

export interface ExploreLevel {
  code: string;
  name: string;
  username: string;
  def: Record<string, unknown>;
  mine: boolean;
  plays: number;
  starOverride: number | null;
  hambis: number | null;
  verified: number;
  /** The level's FIRST approval, `YYYY-MM-DD` ('' if unknown). */
  approvedOn: string;
  description: string;
}

export async function exploreGet(c: SupabaseClient, code: string): Promise<Result<ExploreLevel>> {
  const { data, error } = await c.rpc('explore_get', { p_code: code });
  const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
  if (error || !row || !row.def || typeof row.def !== 'object') {
    return { ok: false, error: "Couldn't load that level — it may have been taken down" };
  }
  return {
    ok: true,
    value: {
      code: str(row.code),
      name: str(row.name),
      username: str(row.username),
      def: row.def as Record<string, unknown>,
      mine: row.mine === true,
      plays: num(row.plays),
      starOverride: numOrNull(row.star_override),
      hambis: numOrNull(row.hambis),
      verified: num(row.verified),
      approvedOn: str(row.approved_on),
      description: str(row.description),
    },
  };
}

/**
 * A win of a published level (signed in only). The server records it once,
 * and says whether THIS win pays: the first win while the level is rated.
 * `hambis` is the server's — pay from it, not from the list loaded earlier.
 */
export async function exploreClear(
  c: SupabaseClient,
  code: string,
): Promise<{ hambis: number | null; pay: boolean } | null> {
  const { data, error } = await c.rpc('explore_clear', { p_code: code });
  const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
  if (error || !row) return null;
  return { hambis: numOrNull(row.hambis), pay: row.pay === true };
}

/** One play (the server skips the creator's own). Fire and forget. */
export async function explorePlay(c: SupabaseClient, code: string): Promise<void> {
  await c.rpc('explore_play', { p_code: code });
}

// ── Admins and devs ──────────────────────────────────────────────────────────

export interface LevelRequest {
  id: string;
  code: string;
  username: string;
  name: string;
  def: Record<string, unknown>;
  kind: 'publish' | 'unban';
  requestedAt: string;
  description: string;
}

export async function listLevelRequests(c: SupabaseClient): Promise<Result<LevelRequest[]>> {
  const { data, error } = await c.rpc('admin_list_level_requests');
  if (error || !Array.isArray(data)) return { ok: false, error: 'Could not load the requests' };
  const out: LevelRequest[] = [];
  for (const r of data as Record<string, unknown>[]) {
    if (typeof r.id !== 'string' || !r.def || typeof r.def !== 'object') continue;
    out.push({
      id: r.id,
      code: str(r.code),
      username: str(r.username),
      name: str(r.name),
      def: r.def as Record<string, unknown>,
      kind: r.request_kind === 'unban' ? 'unban' : 'publish',
      requestedAt: str(r.requested_at),
      description: str(r.description),
    });
  }
  return { ok: true, value: out };
}

/** Approve / disapprove EXACTLY the version shown (its `requestedAt`). */
export async function reviewLevel(
  c: SupabaseClient,
  req: Pick<LevelRequest, 'id' | 'requestedAt'>,
  approve: boolean,
  reason: string,
): Promise<Result<null>> {
  const { error } = await c.rpc('admin_review_level', {
    p_id: req.id,
    p_approve: approve,
    p_reason: reason,
    p_requested_at: req.requestedAt,
  });
  if (error) return { ok: false, error: friendly(error.message, 'Could not save — try again') };
  return { ok: true, value: null };
}

/** `/rate hambi|star|plays <code> <n|none|auto>`: the level's name back. */
export async function rateLevel(
  c: SupabaseClient,
  code: string,
  what: 'hambi' | 'star' | 'plays',
  value: number | null,
): Promise<Result<string>> {
  const { data, error } = await c.rpc('admin_rate_level', {
    p_code: code,
    p_what: what,
    p_value: value,
  });
  if (error) return { ok: false, error: friendly(error.message, 'Could not rate — try again') };
  return { ok: true, value: str(data) };
}

/** `/server ban|unban <code>`: the level's name back. */
export async function serverBan(
  c: SupabaseClient,
  code: string,
  ban: boolean,
): Promise<Result<string>> {
  const { data, error } = await c.rpc('admin_server_ban', { p_code: code, p_ban: ban });
  if (error) return { ok: false, error: friendly(error.message, 'Could not do that — try again') };
  return { ok: true, value: str(data) };
}

/** `/suggest <code> …`: the level's name back. */
export async function suggestLevel(
  c: SupabaseClient,
  code: string,
  kind: 'star' | 'hambi' | 'ban' | 'unban',
  value: number | null,
): Promise<Result<string>> {
  const { data, error } = await c.rpc('dev_suggest_level', {
    p_code: code,
    p_kind: kind,
    p_value: value,
  });
  if (error) return { ok: false, error: friendly(error.message, 'Could not suggest — try again') };
  return { ok: true, value: str(data) };
}

/** A level's map for a /suggest card (admins): the approved, else the waiting one. */
export async function adminLevelDef(
  c: SupabaseClient,
  code: string,
): Promise<{ name: string; def: Record<string, unknown> | null; chapter: boolean } | null> {
  const { data, error } = await c.rpc('admin_level_def', { p_code: code });
  const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
  if (error || !row) return null;
  return {
    name: str(row.name),
    def: row.def && typeof row.def === 'object' ? (row.def as Record<string, unknown>) : null,
    chapter: row.chapter === true,
  };
}

// ── Chapters (spec 2026-11-03 §5) ────────────────────────────────────────────

export const CHAPTER_SAVE_ERRORS: [string, string][] = [
  ['creator_chapters_name', 'You have already named a chapter this name'],
  ['duplicate', 'You have already named a chapter this name'],
  ['too many chapters', 'You can keep 10 chapters — delete one first'],
  ['bad name', 'Give your chapter a name (30 letters at most)'],
  ['bad chapter', "Couldn't save — a chapter holds 30 different levels at most"],
  ['level locked', 'This chapter is locked while an admin reviews your unban request'],
  ['not signed in', 'Sign in to make chapters'],
];

export function chapterSaveError(message: string | undefined): string {
  const m = message ?? '';
  for (const [k, v] of CHAPTER_SAVE_ERRORS) if (m.includes(k)) return v;
  return "Couldn't save your chapter — try again";
}

function rowToChapter(r: Record<string, unknown>): CreatorChapter {
  return {
    id: str(r.id),
    name: str(r.name),
    ...parseChapterDef(r.def),
    updatedAt: Date.parse(str(r.updated_at)) || 0,
  };
}

export async function myChapters(c: SupabaseClient): Promise<Result<CreatorChapter[]>> {
  const { data, error } = await c
    .from('creator_chapters')
    .select('id, name, def, updated_at')
    .order('updated_at', { ascending: false });
  if (error || !Array.isArray(data)) {
    return { ok: false, error: "Couldn't load your chapters — check your connection" };
  }
  return { ok: true, value: (data as Record<string, unknown>[]).map(rowToChapter) };
}

export async function saveChapter(
  c: SupabaseClient,
  id: string,
  name: string,
  def: ChapterDef,
): Promise<Result<string>> {
  const { data, error } = await c.rpc('save_creator_chapter', {
    p_id: id,
    p_name: name,
    p_def: def,
  });
  if (error) return { ok: false, error: chapterSaveError(error.message) };
  return { ok: true, value: str(data) };
}

export async function deleteChapter(c: SupabaseClient, id: string): Promise<Result<null>> {
  const { error } = await c.from('creator_chapters').delete().eq('id', id);
  if (error) return { ok: false, error: "Couldn't delete that chapter — try again" };
  return { ok: true, value: null };
}

export async function myPublishedChapters(c: SupabaseClient): Promise<Result<PublishedRow[]>> {
  const { data, error } = await c.rpc('my_published_chapters');
  if (error || !Array.isArray(data)) return { ok: false, error: 'Could not load' };
  return {
    ok: true,
    value: (data as Record<string, unknown>[]).map((r) => ({
      sourceId: str(r.source_id),
      code: str(r.code),
      live: r.live === true,
      banned: r.banned === true,
      requestKind:
        r.request_kind === 'publish' || r.request_kind === 'unban' ? r.request_kind : null,
      plays: num(r.plays),
    })),
  };
}

export async function publishChapter(c: SupabaseClient, sourceId: string): Promise<Result<string>> {
  const { data, error } = await c.rpc('publish_creator_chapter', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't publish — try again") };
  return { ok: true, value: str(data) };
}

export async function cancelChapterRequest(
  c: SupabaseClient,
  sourceId: string,
): Promise<Result<null>> {
  const { error } = await c.rpc('cancel_chapter_request', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't cancel — try again") };
  return { ok: true, value: null };
}

export async function askUnbanChapter(c: SupabaseClient, sourceId: string): Promise<Result<null>> {
  const { error } = await c.rpc('ask_unban_chapter', { p_source: sourceId });
  if (error) return { ok: false, error: friendly(error.message, "Couldn't ask — try again") };
  return { ok: true, value: null };
}

export function parseExploreChapterRows(data: unknown): ExploreChapterRow[] {
  if (!Array.isArray(data)) return [];
  const out: ExploreChapterRow[] = [];
  for (const r of data as Record<string, unknown>[]) {
    if (!r || typeof r.code !== 'string' || typeof r.name !== 'string') continue;
    out.push({
      code: r.code,
      name: r.name,
      username: str(r.username),
      plays: num(r.plays),
      starOverride: numOrNull(r.star_override),
      hambis: numOrNull(r.hambis),
      trending: r.trending === true,
      border: str(r.border) || 'violet',
      levelCount: num(r.level_count),
    });
  }
  return out;
}

export async function exploreChapters(c: SupabaseClient): Promise<Result<ExploreChapterRow[]>> {
  const { data, error } = await c.rpc('explore_chapters_list');
  if (error) return { ok: false, error: "Couldn't load Explore — check your connection" };
  return { ok: true, value: parseExploreChapterRows(data) };
}

function parseLevelRefs(raw: unknown): ChapterLevelRef[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Record<string, unknown>[])
    .filter((l) => l && typeof l.code === 'string')
    .map((l) => ({ code: str(l.code), name: typeof l.name === 'string' ? l.name : null }));
}

export interface ExploreChapter {
  code: string;
  name: string;
  username: string;
  border: string;
  format: string;
  order: string;
  levels: ChapterLevelRef[];
  plays: number;
  starOverride: number | null;
  hambis: number | null;
}

export async function exploreChapterGet(
  c: SupabaseClient,
  code: string,
): Promise<Result<ExploreChapter>> {
  const { data, error } = await c.rpc('explore_chapter_get', { p_code: code });
  const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
  if (error || !row) {
    return { ok: false, error: "Couldn't load that chapter — it may have been taken down" };
  }
  return {
    ok: true,
    value: {
      code: str(row.code),
      name: str(row.name),
      username: str(row.username),
      border: str(row.border) || 'violet',
      format: str(row.format) || 'episodes',
      order: str(row.ord) || 'roman',
      levels: parseLevelRefs(row.levels),
      plays: num(row.plays),
      starOverride: numOrNull(row.star_override),
      hambis: numOrNull(row.hambis),
    },
  };
}

/** One play of a chapter (the server skips the maker's own). Fire and forget. */
export async function chapterPlay(c: SupabaseClient, code: string): Promise<void> {
  await c.rpc('chapter_play', { p_code: code });
}

export interface ChapterRequest {
  id: string;
  code: string;
  username: string;
  name: string;
  def: ChapterDef;
  levels: ChapterLevelRef[];
  kind: 'publish' | 'unban';
  requestedAt: string;
}

export async function listChapterRequests(c: SupabaseClient): Promise<Result<ChapterRequest[]>> {
  const { data, error } = await c.rpc('admin_list_chapter_requests');
  if (error || !Array.isArray(data)) return { ok: false, error: 'Could not load the requests' };
  const out: ChapterRequest[] = [];
  for (const r of data as Record<string, unknown>[]) {
    if (typeof r.id !== 'string') continue;
    out.push({
      id: r.id,
      code: str(r.code),
      username: str(r.username),
      name: str(r.name),
      def: parseChapterDef(r.def),
      levels: parseLevelRefs(r.levels),
      kind: r.request_kind === 'unban' ? 'unban' : 'publish',
      requestedAt: str(r.requested_at),
    });
  }
  return { ok: true, value: out };
}

export async function reviewChapter(
  c: SupabaseClient,
  req: Pick<ChapterRequest, 'id' | 'requestedAt'>,
  approve: boolean,
  reason: string,
): Promise<Result<null>> {
  const { error } = await c.rpc('admin_review_chapter', {
    p_id: req.id,
    p_approve: approve,
    p_reason: reason,
    p_requested_at: req.requestedAt,
  });
  if (error) return { ok: false, error: friendly(error.message, 'Could not save — try again') };
  return { ok: true, value: null };
}
